import GoogleMapReact from 'google-map-react';
import { useEffect, useRef, useState } from 'react';
import { FaPlug } from 'react-icons/fa';
import { HiOutlineLocationMarker, HiOutlineOfficeBuilding } from 'react-icons/hi';
import { useNavigate } from 'react-router';
import { Box, Button, ButtonGroup, Paper, Stack, Tooltip, Typography, Slider, IconButton, Modal, useTheme, useMediaQuery, Checkbox, FormControlLabel, List, ListItem } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';

// Import the light style array directly
const mapStyle = [
    {
        "featureType": "administrative",
        "elementType": "labels.text.fill",
        "stylers": [{ "color": "#6195a0" }]
    },
    {
        "featureType": "landscape",
        "elementType": "geometry",
        "stylers": [
            { "lightness": "0" },
            { "saturation": "0" },
            { "color": "#f5f5f2" },
            { "gamma": "1" }
        ]
    },
    {
        "featureType": "poi",
        "elementType": "all",
        "stylers": [{ "visibility": "off" }]
    },
    {
        "featureType": "road",
        "elementType": "all",
        "stylers": [
            { "saturation": -100 },
            { "lightness": 45 },
            { "visibility": "simplified" }
        ]
    },
    {
        "featureType": "water",
        "elementType": "all",
        "stylers": [{ "color": "#C1D1D6" }]
    }
];

const defaultCenter = {
    lat: 53.40979,
    lng: -2.15761
};

const defaultZoom = 7;

function Map(props: any) {
    const [selectLocation, setSelectLocation] = useState<any>(null);
    const [showApproved, setShowApproved] = useState(true);
    const [showBusiness, setShowBusiness] = useState(true);
    const [showPotential, setShowPotential] = useState(true);
    const [showInProgress, setShowInProgress] = useState(true);
    const [showNull, setShowNull] = useState(true);
    const [showOther, setShowOther] = useState(true);
    const [showPolygons, setShowPolygons] = useState(true);
    const [showBusinessWorkflowState, setShowBusinessWorkflowState] = useState(true);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const mapRef = useRef<any>(null);
    const polygonsRef = useRef<any[]>([]);
    const showButtons = props.showButtons;
    const [showTooltip, setShowTooltip] = useState(false);
    const [populationFilter, setPopulationFilter] = useState(100); // Default to max value

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleApiLoaded = (map: any, maps: any) => {
        mapRef.current = { map, maps };
        renderPolygons();
    };

    const Marker = ({ no_visit }: any) => (
        <Box sx={{
            margin: "-10px 0 0 -10px",
            p: 2.5,
            width: 20,
            height: 20,
            '& .marker': {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }
        }}>
            <Typography
                variant="h6"
                sx={{
                    color: 'white',
                    fontWeight: 800,
                    fontSize: 20,
                }}
            >
                {no_visit}
            </Typography>
        </Box>
    );

    const Partner = (props: any) => {
        const navigate = useNavigate();

        const handleClick = () => {
            // if (props.info.workflowState === 'AGREED') {
            //     navigate('/share/business/make-advanced-booking', { state: { ...props.info } });
            // }
        };

        return (
            <Box
                onMouseEnter={() => !isMobile && setShowTooltip(true)}
                onMouseLeave={() => !isMobile && setShowTooltip(false)}
                // onClick={handleClick}
                sx={{ position: 'relative' }}
            >
                <Box className={`marker ${props.info.workflowState}`} sx={{ m: 0, width: 24, height: 24 }}>
                    <HiOutlineLocationMarker
                        color={
                            props.info.workflowState === 'AGREED'
                                ? 'green'
                                : props.info.workflowState === 'POTENTIAL'
                                    ? 'grey'
                                    : 'grey'
                        }
                        size='20'
                    />
                </Box>
                {showTooltip && !isMobile && (
                    <Paper
                        elevation={3}
                        sx={{
                            position: 'absolute',
                            top: '30px',
                            left: '-24px',
                            p: 0.5,
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                        }}
                    >
                        <Typography variant="body2">
                            {showBusinessWorkflowState ? props.info.businessName : props.info.workflowState}
                        </Typography>
                    </Paper>
                )}
            </Box>
        );
    };

    const Business = (props: any) => (
        <Box sx={{ '& .marker': { margin: "-20px 0 0 -20px", width: 24, height: 24 } }}>
            <HiOutlineOfficeBuilding color='blue' size='20' />
        </Box>
    );

    const ChargePoint = (props: any) => (
        <Tooltip
            open={!isMobile && showTooltip}
            onOpen={() => !isMobile && setShowTooltip(true)}
            onClose={() => !isMobile && setShowTooltip(false)}
            title={props.info?.name || ''}
        >
            <Box sx={{ '& .marker': { margin: '-20px 0 0 -20px', width: 24, height: 24 } }}>
                <HiOutlineLocationMarker color="blue" size="20" />
            </Box>
        </Tooltip>
    );

    const SelectMarker = (props: any) => (
        <Box sx={{
            '& .marker': {
                margin: "-20px 0 0 -20px",
                height: 40,
                width: 40,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }
        }}>
            <Typography variant="h4" sx={{ color: 'white' }}>S</Typography>
        </Box>
    );

    useEffect(() => {
        renderPolygons();
    }, [showApproved, showBusiness, showPotential, showNull, showOther, showPolygons, showInProgress]);

    const processPolygons = (polygonsData: any, map: any, maps: any, options: any) => {
        const {
            showApproved,
            showBusiness,
            showPotential,
            showInProgress,
            showNull,
            showOther,
            polygonsRef
        } = options;

        polygonsData.forEach((polygonData: any) => {
            let strokeColor = "blue";
            let fillColor = "blue";

            if (polygonData.workflowState === 'AGREED') {
                strokeColor = "green";
                fillColor = "green";
            } else if (polygonData.workflowState === 'POTENTIAL') {
                strokeColor = "grey";
                fillColor = "grey";
            } else if (polygonData.workflowState === 'OFFERED' || polygonData.workflowState === 'REQUESTED') {
                strokeColor = "orange";
                fillColor = "orange";
            }

            const shouldDisplay = (
                (polygonData.workflowState === 'AGREED' && showApproved) ||
                (polygonData.workflowState === 'POTENTIAL' && showPotential) ||
                ((polygonData.workflowState === 'OFFERED' || polygonData.workflowState === 'REQUESTED') && showInProgress) ||
                (polygonData.workflowState == null && showBusiness) ||
                (polygonData.workflowState !== 'AGREED' && polygonData.workflowState !== 'POTENTIAL' && polygonData.workflowState !== 'OFFERED' && polygonData.workflowState !== 'REQUESTED' && polygonData.workflowState != null && showOther)
            );

            if (polygonData.coords && polygonData.coords.length > 0 && shouldDisplay) {
                const polygon = new maps.Polygon({
                    paths: polygonData.coords,
                    strokeColor: strokeColor,
                    strokeOpacity: 0.5,
                    strokeWeight: 1,
                    fillColor: fillColor,
                    fillOpacity: 0.1
                });

                polygon.setMap(map);
                polygonsRef.current.push(polygon);
            }
        });
    };

    const renderPolygons = () => {
        if (!mapRef.current) return;

        const { map, maps } = mapRef.current;

        // Clear existing polygons
        polygonsRef.current.forEach(polygon => polygon.setMap(null));
        polygonsRef.current = [];

        if (showPolygons) {
            // Process business polygons
            if (props.businessPolygons && props.businessPolygons.length > 0) {
                processPolygons(props.businessPolygons, map, maps, {
                    showApproved,
                    showBusiness,
                    showPotential,
                    showInProgress,
                    showNull,
                    showOther,
                    polygonsRef,
                });
            }

            // Process charging partner polygons
            if (props.chargingPartnerPolygons && props.chargingPartnerPolygons.length > 0) {
                processPolygons(props.chargingPartnerPolygons, map, maps, {
                    showApproved,
                    showBusiness,
                    showPotential,
                    showInProgress,
                    showNull,
                    showOther,
                    polygonsRef,
                });
            }
        }
    };

    const defaultProps = {
        center: props.center ? props.center : {
            lat: 53.40979,
            lng: -2.15761
        },
        zoom: props.zoom ? props.zoom : 7
    };

    function handleMapClick(obj: any) {
        // setSelectLocation(<SelectMarker lat={obj.lat} lng={obj.lng} />);
    }

    useEffect(() => {
        setSelectLocation(<></>);
    }, [props.enableChangeLocation]);

    const filteredPartnerLocations = props.partnerLocations?.filter((location: any) => {
        if (location.workflowState === 'AGREED' && showApproved) return true;
        if (location.workflowState === 'POTENTIAL' && showPotential) return true;
        if ((location.workflowState === 'OFFERED' || location.workflowState === 'REQUESTED') && showInProgress) return true;
        if (location.workflowState == null && showNull) return true;
        if (location.workflowState !== 'AGREED' && location.workflowState !== 'POTENTIAL' && location.workflowState !== 'OFFERED' && location.workflowState !== 'REQUESTED' && location.workflowState != null && showOther) return true;
        return false;
    });

    const filteredChargePoints = props.chargePoints?.filter((location: any) => {
        if (location.workflowState === 'AGREED' && showApproved) return true;
        if (location.workflowState === 'POTENTIAL' && showPotential) return true;
        if ((location.workflowState === 'OFFERED' || location.workflowState === 'REQUESTED') && showInProgress) return true;
        if (location.workflowState == null && showNull) return true;
        if (location.workflowState !== 'AGREED' && location.workflowState !== 'POTENTIAL' && location.workflowState !== 'OFFERED' && location.workflowState !== 'REQUESTED' && location.workflowState != null && showOther) return true;
        return false;
    });

    const filteredBusinessLocations = props.businessLocations?.filter((location: any) => {
        if (showBusiness) return true;
        return false;
    });

    // Determine marker color based on population
    const getMarkerColor = (range: number) => {
        if (range <= 10) return '#bef213'; // Dark red for large populations
        if (range <= 20) return '#cbe100'; // Dark red for large populations
        if (range <= 30) return '#d6cf00'; // Firebrick red
        if (range <= 40) return '#e0bd00'; // Firebrick red
        if (range <= 50) return '#e8aa00'; // Firebrick red
        if (range <= 60) return '#f09500'; // Firebrick red
        if (range <= 70) return '#f77e00'; // Firebrick red
        if (range <= 80) return '#fb6400'; // Firebrick red
        if (range <= 90) return '#fe4400';  // IndianRed
        if (range > 90) return '#ff0000';  // IndianRed
        return 'orange';  // Light red for smaller populations
    };

    const CityMarker = ({ divert }: any) => (
        <div>
            <FaPlug
                color={getMarkerColor(divert.range)}
                size="24"
            // title={`${city.name} (Population: ${city.population.toLocaleString()})`}
            />
        </div>
    );

    const filteredDiverts = props.diverts?.filter((divert: any) => divert.range <= populationFilter);

    const FilterContent = () => (
        <Stack spacing={2}>
            {/* Desktop View - Buttons */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <ButtonGroup size="small" orientation="vertical" variant="outlined" fullWidth>
                    <Button
                        onClick={() => setShowApproved(!showApproved)}
                        variant={showApproved ? "contained" : "outlined"}
                    >
                        {showApproved ? 'Hide Network' : 'Show Network'}
                    </Button>
                    <Button
                        onClick={() => setShowBusiness(!showBusiness)}
                        variant={showBusiness ? "contained" : "outlined"}
                    >
                        {showBusiness ? 'Hide Business' : 'Show Business'}
                    </Button>
                    <Button
                        onClick={() => setShowPotential(!showPotential)}
                        variant={showPotential ? "contained" : "outlined"}
                    >
                        {showPotential ? 'Hide Potential Partners' : 'Show Potential Partners'}
                    </Button>
                    <Button
                        onClick={() => setShowInProgress(!showInProgress)}
                        variant={showInProgress ? "contained" : "outlined"}
                    >
                        {showInProgress ? 'Hide In Progress Sites' : 'Show In Progress Sites'}
                    </Button>
                    <Button
                        onClick={() => setShowPolygons(!showPolygons)}
                        variant={showPolygons ? "contained" : "outlined"}
                    >
                        {showPolygons ? 'Hide Polygons' : 'Show Polygons'}
                    </Button>
                    <Button
                        onClick={() => setShowBusinessWorkflowState(!showBusinessWorkflowState)}
                        variant={showBusinessWorkflowState ? "contained" : "outlined"}
                    >
                        {showBusinessWorkflowState ? 'Show Workflow State' : 'Show Business Name'}
                    </Button>
                </ButtonGroup>
            </Box>

            {/* Mobile View - Checkboxes */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <List sx={{ py: 0 }}>
                    <ListItem sx={{ px: 0, py: 0 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showApproved}
                                    onChange={() => setShowApproved(!showApproved)}
                                    sx={{
                                        color: '#bef213',
                                        '&.Mui-checked': {
                                            color: '#bef213',
                                        },
                                    }}
                                />
                            }
                            label="Network"
                        />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showBusiness}
                                    onChange={() => setShowBusiness(!showBusiness)}
                                    sx={{
                                        color: '#bef213',
                                        '&.Mui-checked': {
                                            color: '#bef213',
                                        },
                                    }}
                                />
                            }
                            label="Business"
                        />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showPotential}
                                    onChange={() => setShowPotential(!showPotential)}
                                    sx={{
                                        color: '#bef213',
                                        '&.Mui-checked': {
                                            color: '#bef213',
                                        },
                                    }}
                                />
                            }
                            label="Potential Partners"
                        />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showInProgress}
                                    onChange={() => setShowInProgress(!showInProgress)}
                                    sx={{
                                        color: '#bef213',
                                        '&.Mui-checked': {
                                            color: '#bef213',
                                        },
                                    }}
                                />
                            }
                            label="In Progress Sites"
                        />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showPolygons}
                                    onChange={() => setShowPolygons(!showPolygons)}
                                    sx={{
                                        color: '#bef213',
                                        '&.Mui-checked': {
                                            color: '#bef213',
                                        },
                                    }}
                                />
                            }
                            label="Polygons"
                        />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showBusinessWorkflowState}
                                    onChange={() => setShowBusinessWorkflowState(!showBusinessWorkflowState)}
                                    sx={{
                                        color: '#bef213',
                                        '&.Mui-checked': {
                                            color: '#bef213',
                                        },
                                    }}
                                />
                            }
                            label="Show Workflow State"
                        />
                    </ListItem>
                </List>
            </Box>

            {props.diverts && (
                <Box sx={{
                    px: { xs: 0, md: 2 }, // No padding on mobile
                    width: '100%',
                    boxSizing: 'border-box'
                }}>
                    <Typography variant="body2" gutterBottom>
                        Filter Charging Detour: {populationFilter} km
                    </Typography>
                    <Box sx={{
                        width: '100%',
                        px: { xs: 0, md: 1 }, // No horizontal padding on mobile
                        boxSizing: 'border-box',
                        overflow: 'hidden' // Prevent overflow
                    }}>
                        <Slider
                            value={populationFilter}
                            onChange={(_, value) => setPopulationFilter(value as number)}
                            min={0}
                            max={100}
                            step={10}
                            valueLabelDisplay="off"
                            disableSwap={false}
                            track={false}

                        />
                    </Box>
                </Box>
            )}
        </Stack>
    );

    return (
        <Box sx={{
            height: { xs: 'calc(100vh - 64px - 64px)', md: 'calc(100vh - 140px)' }, // Account for AppBar (64px) and TitleBar (64px) on mobile
            width: '100%',
            position: 'relative',
            p: { xs: 0, md: 3 }, // No padding on mobile, 24px padding on desktop
            pb: { xs: 0, md: 3 }, // No bottom padding on mobile, 24px on desktop
            boxSizing: 'border-box',
            margin: 0,
            overflow: 'hidden' // Prevent scrolling
        }}>
            {showButtons && (
                <>
                    {/* Desktop Filter Panel */}
                    <Stack spacing={2} sx={{
                        position: 'absolute',
                        top: 48,  // Changed from 24 to 48
                        left: 48, // Changed from 24 to 48
                        zIndex: 1,
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        boxShadow: 1,
                        width: 300,
                        display: { xs: 'none', md: 'flex' } // Hide on mobile
                    }}>
                        <FilterContent />
                    </Stack>

                    {/* Mobile Filter Icon */}
                    <IconButton
                        onClick={() => setFilterModalOpen(true)}
                        sx={{
                            position: 'absolute',
                            top: { xs: 16, md: 48 }, // Closer to edge on mobile
                            left: { xs: 16, md: 48 }, // Closer to edge on mobile
                            zIndex: 1,
                            bgcolor: '#bef213', // Evata green background
                            boxShadow: 1,
                            display: { xs: 'flex', md: 'none' }, // Show only on mobile
                            color: 'black', // Black icon
                            '&:hover': {
                                bgcolor: 'rgba(190, 242, 19, 0.8)' // Slightly transparent on hover
                            }
                        }}
                    >
                        <FilterListIcon sx={{ fontSize: 28 }} />
                    </IconButton>

                    {/* Mobile Filter Modal */}
                    <Modal
                        open={filterModalOpen}
                        onClose={() => setFilterModalOpen(false)}
                        aria-labelledby="filter-modal-title"
                        sx={{
                            display: { xs: 'flex', md: 'none' }
                        }}
                    >
                        <Box sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '95%', // Increased from 90%
                            maxWidth: 450, // Increased from 400
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            boxShadow: 24,
                            p: { xs: 2, sm: 3 }, // Reduced padding on mobile
                            maxHeight: '85vh', // Increased from 80vh
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {/* Modal Header */}
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 2,
                                pb: 1,
                                borderBottom: 1,
                                borderColor: 'divider',
                                flexShrink: 0
                            }}>
                                <Typography variant="h6" component="h2">
                                    Map Filters
                                </Typography>
                                <IconButton
                                    onClick={() => setFilterModalOpen(false)}
                                    size="small"
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Box>

                            {/* Filter Content */}
                            <Box sx={{
                                overflow: 'auto',
                                flex: 1,
                                pr: 0.5 // Reduced right padding
                            }}>
                                <FilterContent />
                            </Box>
                        </Box>
                    </Modal>
                </>
            )}
            <Box sx={{
                height: '100%',
                width: '100%',
                position: 'relative',
                padding: 0,
                margin: 0,
                border: 'none'
            }}>
                <GoogleMapReact
                    bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_MAP_KEY || '' }}
                    defaultCenter={props.center || defaultCenter}
                    defaultZoom={props.zoom || defaultZoom}
                    yesIWantToUseGoogleMapApiInternals
                    onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
                    options={{
                        styles: mapStyle,
                        fullscreenControl: false,
                        gestureHandling: 'cooperative',
                        zoomControl: true
                    }}
                // onClick={(obj) => {
                //     if (props.enableChangeLocation) {
                //         handleMapClick(obj);
                //     }
                // }}
                >
                    {filteredDiverts?.map((divert: any, index: number) => (
                        <CityMarker
                            key={index}
                            lat={divert.latitude}
                            lng={divert.longitude}
                            divert={divert}
                        />
                    ))}
                    {selectLocation && selectLocation}
                    {filteredPartnerLocations?.map((e: any) => (
                        <Partner
                            key={e.uuid}
                            lat={e.centreLatitude}
                            lng={e.centreLongitude}
                            info={e}
                        />
                    ))}
                    {filteredChargePoints?.map((e: any) => (
                        <ChargePoint
                            key={e.uuid}
                            lat={e.centreLatitude}
                            lng={e.centreLongitude}
                            info={e}
                        />
                    ))}
                    {filteredBusinessLocations?.map((e: any) => (
                        <Business
                            key={e.uuid}
                            lat={e.centreLatitude}
                            lng={e.centreLongitude}
                        />
                    ))}
                    {props.vehicleMapData?.map((e: any) => (
                        <Marker
                            key={e.uuid}
                            lat={e.centreLatitude}
                            lng={e.centreLongitude}
                            no_visit={e.visitCount}
                        />
                    ))}
                </GoogleMapReact>
                {props.children}
            </Box>
        </Box>
    );
}

export default Map;
